import React, { useRef } from 'react';
import { Box, Divider, IconButton, Stack, Tooltip } from '@mui/material';
import type { Editor } from '@tiptap/react';
import FormatBoldOutlinedIcon from '@mui/icons-material/FormatBoldOutlined';
import FormatItalicOutlinedIcon from '@mui/icons-material/FormatItalicOutlined';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import FormatListNumberedOutlinedIcon from '@mui/icons-material/FormatListNumberedOutlined';
import TitleOutlinedIcon from '@mui/icons-material/TitleOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import RedoOutlinedIcon from '@mui/icons-material/RedoOutlined';

export interface RichTextToolbarProps {
    editor: Editor | null;
    enableImage?: boolean;
    onInsertImageFiles?: (files: File[]) => void;
    disabled?: boolean;
}

function ToolbarBtn(props: {
    title: string;
    active?: boolean;
    disabled?: boolean;
    onClick: () => void;
    children: React.ReactNode;
}) {
    const { title, active, disabled, onClick, children } = props;
    return (
        <Tooltip title={title}>
            <span>
                <IconButton
                    size="small"
                    disabled={disabled}
                    onClick={onClick}
                    color={active ? 'primary' : 'default'}
                    aria-label={title}
                >
                    {children}
                </IconButton>
            </span>
        </Tooltip>
    );
}

export function RichTextToolbar({
    editor,
    enableImage = true,
    onInsertImageFiles,
    disabled,
}: RichTextToolbarProps): React.ReactElement | null {
    const fileRef = useRef<HTMLInputElement>(null);
    if (!editor) return null;

    const setLink = () => {
        const prev = editor.getAttributes('link').href as string | undefined;
        const url = window.prompt('URL', prev || 'https://');
        if (url === null) return;
        if (url.trim() === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
    };

    return (
        <Box sx={{ px: 0.5, py: 0.25 }}>
            <Stack direction="row" spacing={0.25} alignItems="center" flexWrap="wrap">
                <ToolbarBtn
                    title="Bold"
                    active={editor.isActive('bold')}
                    disabled={disabled}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                >
                    <FormatBoldOutlinedIcon fontSize="small" />
                </ToolbarBtn>
                <ToolbarBtn
                    title="Italic"
                    active={editor.isActive('italic')}
                    disabled={disabled}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                >
                    <FormatItalicOutlinedIcon fontSize="small" />
                </ToolbarBtn>
                <ToolbarBtn
                    title="Heading"
                    active={editor.isActive('heading', { level: 3 })}
                    disabled={disabled}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                >
                    <TitleOutlinedIcon fontSize="small" />
                </ToolbarBtn>
                <ToolbarBtn
                    title="Bullet list"
                    active={editor.isActive('bulletList')}
                    disabled={disabled}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                >
                    <FormatListBulletedOutlinedIcon fontSize="small" />
                </ToolbarBtn>
                <ToolbarBtn
                    title="Ordered list"
                    active={editor.isActive('orderedList')}
                    disabled={disabled}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                >
                    <FormatListNumberedOutlinedIcon fontSize="small" />
                </ToolbarBtn>
                <ToolbarBtn
                    title="Link"
                    active={editor.isActive('link')}
                    disabled={disabled}
                    onClick={setLink}
                >
                    <LinkOutlinedIcon fontSize="small" />
                </ToolbarBtn>
                {enableImage && (
                    <ToolbarBtn
                        title="Insert image"
                        disabled={disabled}
                        onClick={() => fileRef.current?.click()}
                    >
                        <ImageOutlinedIcon fontSize="small" />
                    </ToolbarBtn>
                )}
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <ToolbarBtn
                    title="Undo"
                    disabled={disabled || !editor.can().undo()}
                    onClick={() => editor.chain().focus().undo().run()}
                >
                    <UndoOutlinedIcon fontSize="small" />
                </ToolbarBtn>
                <ToolbarBtn
                    title="Redo"
                    disabled={disabled || !editor.can().redo()}
                    onClick={() => editor.chain().focus().redo().run()}
                >
                    <RedoOutlinedIcon fontSize="small" />
                </ToolbarBtn>
            </Stack>
            <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => {
                    const list = e.target.files;
                    if (list && list.length > 0 && onInsertImageFiles) {
                        onInsertImageFiles(Array.from(list));
                    }
                    e.target.value = '';
                }}
            />
        </Box>
    );
}
